import React from 'react';
import Footer from '../../components/Footer';
import HeroSection from './HeroSection';
import ExpertSlider from '../../components/ExpertSlider';
import TestimonialsSection from './TestimonialsSection';
import { 
  FaStethoscope, 
  FaTractor, 
  FaShieldVirus, 
  FaNetworkWired, 
  FaChalkboardTeacher, 
  FaAmbulance,
  FaMobileAlt,
  FaGooglePlay,
  FaApple
} from 'react-icons/fa';

const Home = () => {
  return (
    <div className="font-sans">
      <HeroSection />
      <ServicesSection />
      <ExpertSlider />
      <AboutSection />
      <TestimonialsSection />
      <StatsSection />
      <MobileAppSection />   {/* ← New Section Added */}
      <CTASection />
      <Footer />
    </div>
  );
};

// Services Section (unchanged)
const ServicesSection = () => {
  const services = [
    {
      title: 'Veterinary Care',
      description: 'Comprehensive health services for poultry, including disease diagnosis, vaccination, and treatment plans to ensure optimal flock health.',
      detail: 'Our certified vets provide 24/7 support and personalized care plans.',
      icon: <FaStethoscope className="text-primary text-2xl mb-2" />,
      image: '/assets/images/vaternity-care.jpg',
    },
    {
      title: 'Farm Management Consulting',
      description: 'Expert advice on sustainable farming practices, productivity optimization, and resource management tailored to your farm’s needs.',
      detail: 'Get customized strategies to boost efficiency and profitability.',
      icon: <FaTractor className="text-primary text-2xl mb-2" />,
      image: '/assets/images/farming-consultation.jpg',
    },
    {
      title: 'Disease Control',
      description: 'Proactive strategies and monitoring to prevent and manage outbreaks, ensuring the safety and longevity of your livestock.',
      detail: 'Advanced biosecurity measures to protect your flock year-round.',
      icon: <FaShieldVirus className="text-primary text-2xl mb-2" />,
      image: '/assets/images/disease-control.jpg',
    },
    {
      title: 'IoT Services by ReNile',
      description: 'At ReNile, we provide and develop advanced IoT technologies to boost efficiency and productivity in the agricultural sector.',
      detail: 'In partnership with ReNile in the United Kingdom, we deliver a full range of ReNile services to support modern agriculture.',
      icon: <FaNetworkWired className="text-primary text-2xl mb-2" />,
      image: '/assets/images/renile.png',
      link: 'https://renile.net/',
    },
    {
      title: 'Training & Workshops',
      description: 'Hands-on training for farm staff on best practices in poultry care and modern farming techniques.',
      detail: 'Workshops scheduled monthly to upskill your team.',
      icon: <FaChalkboardTeacher className="text-primary text-2xl mb-2" />,
      image: '/assets/images/training.jpg',
    },
    {
      title: 'Emergency Services',
      description: '24/7 emergency response for urgent veterinary needs, ensuring rapid support for your farm.',
      detail: 'On-call experts available to handle crises at any time.',
      icon: <FaAmbulance className="text-primary text-2xl mb-2" />,
      image: '/assets/images/emergency-service.jpg',
    },
  ];

  return (
    <section className="py-16 bg-gray-100">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-primary mb-12">
          Our Services
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-shadow duration-300"
            >
              <div className="flex justify-center mb-4 text-secondary text-4xl">
                {service.icon}
              </div>

              {service.link ? (
                <a
                  href={service.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xl font-semibold text-primary mb-2 hover:underline"
                >
                  {service.title}
                </a>
              ) : (
                <h3 className="text-xl font-semibold text-primary mb-2">
                  {service.title}
                </h3>
              )}

              <p className="text-gray-600">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Us Section
const AboutSection = () => {
  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4 flex flex-col md:flex-row items-center">
        <div className="md:w-1/2 mb-8 md:mb-0">
          <img
            src="./assets/images/aboutUs.jpg"
            alt="Chicken Farm"
            className="rounded-lg shadow-lg w-full h-auto"
          />
        </div>
        <div className="md:w-1/2 md:pl-8">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">About Us</h2>
          <p className="text-gray-600 mb-4">
            With over a decade of experience, we specialize in chicken farm management and veterinary care. Our mission is to ensure healthy flocks and sustainable farming practices through expert guidance and cutting-edge solutions.
          </p>
          <p className="text-gray-600 mb-4">
            Our team of certified veterinarians and poultry experts is dedicated to helping farmers achieve optimal productivity and animal welfare.
          </p>
          <p className="text-gray-600 mb-4">
            Beyond poultry, we are committed to supporting all types of farming operations. Whether you're managing a small farm or a large-scale enterprise, we offer tailored solutions to meet your unique needs. From health monitoring to optimized feeding strategies, our comprehensive approach improves both yield and livestock wellbeing.
          </p>
          <p className="text-gray-600 mb-4">
            We also provide specialized veterinary services for pets. If you require expert care for your animals, our veterinary specialists are available to diagnose, treat, and support your pet’s health with compassion and professionalism.
          </p>
          <p className="text-gray-600 mb-4">
            Additionally, we offer a unique platform for farmers looking to sell agricultural waste. Simply register, submit a request to sell your farm waste, and local waste collectors will place bids. Once approved, the collectors will handle the pickup directly from your farm—turning waste into opportunity.
          </p>
          <p className="text-gray-600">
            Our goal is to empower farmers with smart tools, expert services, and sustainable practices that benefit both their business and the environment.
          </p>
        </div>
      </div>
    </section>
  );
};

// New Mobile App Section
const MobileAppSection = () => {
  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <FaMobileAlt className="text-primary text-6xl" />
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
            Download Our Mobile App
          </h2>
          
          <p className="text-gray-600 text-lg mb-10 max-w-2xl mx-auto">
            Manage your farm on the go! Track flock health, get instant alerts, 
            access expert advice, and much more with our powerful Chick Farm mobile application.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            {/* Android Button */}
            <a
              href="https://play.google.com/store/apps/details?id=com.chickfarm.app&hl=en"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-black text-white px-8 py-4 rounded-2xl hover:bg-gray-900 transition-all duration-300 group"
            >
              <FaGooglePlay className="text-3xl group-hover:scale-110 transition-transform" />
              <div className="text-left">
                <p className="text-xs opacity-75">GET IT ON</p>
                <p className="text-xl font-semibold tracking-wide">Google Play</p>
              </div>
            </a>

            {/* iOS Button */}
            <a
              href="https://apps.apple.com/us/app/chick-farm/id6757654898"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-black text-white px-8 py-4 rounded-2xl hover:bg-gray-900 transition-all duration-300 group"
            >
              <FaApple className="text-4xl group-hover:scale-110 transition-transform" />
              <div className="text-left">
                <p className="text-xs opacity-75">Download on the</p>
                <p className="text-xl font-semibold tracking-wide">App Store</p>
              </div>
            </a>
          </div>

          <p className="text-sm text-gray-500 mt-8">
            Available for Android and iOS devices
          </p>
        </div>
      </div>
    </section>
  );
};

// Stats/Impact Section
const StatsSection = () => {
  const stats = [
    { value: '10+', label: 'Years of Experience' },
    { value: '500+', label: 'Farms Served' },
    { value: '10K+', label: 'Healthy Chickens' },
    { value: '100%', label: 'Client Satisfaction' },
  ];

  return (
    <section className="py-16 bg-secondary text-white">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-primary mb-12">
          Our Impact
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <h3 className="text-4xl font-bold mb-2 text-white">{stat.value}</h3>
              <p className="text-lg text-white">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Call-to-Action Section
const CTASection = () => {
  return (
    <section className="py-16 bg-secondary text-white">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-primary">
          Ready to Optimize Your Farm?
        </h2>
        <p className="text-lg mb-6 text-white">
          Contact our team of experts today to schedule a consultation or learn more about our services.
        </p>
        <a
          href="/contact"
          className="inline-block bg-white text-primary font-semibold py-3 px-6 rounded-lg hover:bg-gray-100 transition-colors duration-300"
          aria-label="Contact us"
        >
          Get in Touch
        </a>
      </div>
    </section>
  );
};

export default Home;